import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Empty, Table, Tag, Tooltip } from "antd";
import { FieldTimeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import lib from "../../assets/utils/lib";
import DashboardSection from "../dashboard/components/DashboardSection";
import { RowListSkeleton } from "../dashboard/components/DashboardSkeleton";
import { formatPaDuration } from "./paDisplayUtils";
import "./pa-turnaround.css";

/**
 * PA turnaround time (PA Turnaround Time Requirements §4, review §4): how long
 * Customer Support takes from a facility submitting an item (Received) to the
 * decision being submitted (Processed). Driven entirely by the PA dashboard's
 * main filter — no filters of its own:
 *   - the time range selects PROCESSED dates: one month, several, or any range;
 *     the trend, cards and staff table show only what is inside it;
 *   - organisation, plan, facility and check-in type narrow everything;
 *   - the main filter's staff selector applies to this section only.
 * Pending items are the current queue: they follow organisation, plan,
 * facility and type, but not dates or staff, and the card says so.
 * Test facilities, enrollees, policies, organisations and staff accounts are
 * excluded by the API. Figures come from GET /v2/admin/pa/turnaround.
 */

const tatLabel = (seconds) => (seconds == null ? "—" : formatPaDuration(seconds) || "<1m");

/** Main-filter params the turnaround report understands. */
const TAT_PARAM_KEYS = ["date_from", "date_to", "org_id", "plan_type", "plan_type_id", "plan_ids", "location_id", "checkin_type_ids"];

export default function PATurnaroundSection({ filterParams = {}, staffId = null, staffName = null, onStaffOptions, rangeLabel }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [failed, setFailed] = useState(false);

  const params = useMemo(() => {
    const p = {};
    TAT_PARAM_KEYS.forEach((k) => {
      if (filterParams[k] != null && filterParams[k] !== "") p[k] = filterParams[k];
    });
    if (staffId) p.staff_user_id = staffId;
    return p;
  }, [filterParams, staffId]);
  const paramsKey = JSON.stringify(params);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    const res = await lib.paTurnaround(JSON.parse(paramsKey));
    if (res?.error || !res?.data?.data) {
      setFailed(true);
      setReport(null);
    } else {
      setReport(res.data.data);
      if (onStaffOptions) onStaffOptions(res.data.data.staff_options || []);
    }
    setLoading(false);
  }, [paramsKey, onStaffOptions]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = report?.summary || {};
  const notes = report?.notes || {};
  const trend = useMemo(() => report?.trend || [], [report]);
  const maxTrend = useMemo(() => Math.max(1, ...trend.map((t) => t.avg_tat_seconds || 0)), [trend]);

  const staffColumns = [
    {
      title: "Staff member",
      dataIndex: "staff_name",
      key: "staff_name",
      ellipsis: true,
      render: (name, r) => <span title={r.staff_email || name}>{name}</span>,
    },
    {
      title: "Avg turnaround",
      dataIndex: "avg_tat_seconds",
      key: "avg",
      align: "right",
      width: 120,
      sorter: (a, b) => a.avg_tat_seconds - b.avg_tat_seconds,
      render: (v) => tatLabel(v),
    },
    {
      title: "Processed",
      dataIndex: "processed_items",
      key: "processed",
      align: "right",
      width: 96,
      defaultSortOrder: "descend",
      sorter: (a, b) => a.processed_items - b.processed_items,
    },
    { title: "Approved", dataIndex: "approved_items", key: "approved", align: "right", width: 92 },
    { title: "Rejected", dataIndex: "rejected_items", key: "rejected", align: "right", width: 88 },
  ];

  return (
    <DashboardSection
      id="turnaround"
      icon={<FieldTimeOutlined />}
      title="PA turnaround time"
      subtitle="Item received → item processed · grouped by month processed · follows the filters above"
      helpText="Each approved or rejected item counts once, at its first decision by Customer Support, from the time the facility submitted that item. Automatic approvals and manual PAs are excluded, as are test facilities, enrollees, organisations and staff accounts. Pending and queried items are never in averages. Times are Africa/Lagos."
      collapsible={false}
      variant="content"
    >
      <div className="pa-tat__applied" aria-live="polite">
        <span>Processed {rangeLabel || "in the selected range"}</span>
        {staffId ? (
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
            Staff: {staffName || `#${staffId}`} — turnaround section only
          </Tag>
        ) : null}
      </div>

      {loading && !report ? (
        <RowListSkeleton rows={4} />
      ) : failed ? (
        <Empty description="Turnaround time is not available right now" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="pa-tat">
          <div className="pa-tat__stats">
            <div className="pa-tat__stat">
              <span className="pa-tat__stat-label">Average turnaround</span>
              <span className="pa-tat__stat-value">{tatLabel(summary.avg_tat_seconds)}</span>
              <span className="pa-tat__stat-hint">Approved and rejected items</span>
            </div>
            <div className="pa-tat__stat">
              <span className="pa-tat__stat-label">Items processed</span>
              <span className="pa-tat__stat-value">{(summary.processed_items ?? 0).toLocaleString()}</span>
              <span className="pa-tat__stat-hint">
                {(summary.approved_items ?? 0).toLocaleString()} approved · {(summary.rejected_items ?? 0).toLocaleString()} rejected
              </span>
            </div>
            <div className="pa-tat__stat">
              <span className="pa-tat__stat-label">
                Pending items{" "}
                <Tooltip title={notes.pending || "Current queue: follows organisation, plan, facility and type, not dates or staff."}>
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
              <span className="pa-tat__stat-value">{(summary.pending_items ?? 0).toLocaleString()}</span>
              <span className="pa-tat__stat-hint">Current queue · not filtered by date or staff</span>
            </div>
          </div>

          <div className="pa-tat__panels">
            <div className="pa-tat__panel">
              <h4 className="pa-tat__panel-title">Average turnaround by month processed</h4>
              {trend.length ? (
                <ul className="pa-tat__trend" aria-label="Average turnaround by month processed">
                  {trend.map((t) => (
                    <li key={t.month} className="pa-tat__trend-row">
                      <span className="pa-tat__trend-month">{dayjs(`${t.month}-01`).format("MMM YYYY")}</span>
                      <span className="pa-tat__trend-bar">
                        <span style={{ width: `${Math.max(2, (100 * (t.avg_tat_seconds || 0)) / maxTrend)}%` }} />
                      </span>
                      <span className="pa-tat__trend-value">{tatLabel(t.avg_tat_seconds)}</span>
                      <span className="pa-tat__trend-count">{t.processed_items.toLocaleString()} items</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty description="No items processed in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
            <div className="pa-tat__panel">
              <h4 className="pa-tat__panel-title">By Customer Support staff</h4>
              <div className="pa-tat__table-wrap">
                <Table
                  size="small"
                  rowKey={(r) => `${r.staff_user_id ?? "unknown"}`}
                  columns={staffColumns}
                  dataSource={report?.staff || []}
                  pagination={false}
                  tableLayout="fixed"
                  scroll={{ x: 520 }}
                  locale={{ emptyText: "No processed items for these filters" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardSection>
  );
}
